import type { AxiosError } from 'axios'

// axios reuses `ECONNABORTED` for a call that timed out, which is a real drop.
const AXIOS_TIMEOUT_MESSAGE = /^timeout of \d+ms exceeded/

/**
 * True when we stopped this call ourselves.
 * A normal stop is `CanceledError` or an aborted signal.
 * A timeout is not a stop, so it still retries and still reaches Sentry.
 *
 * Kept free of store imports on purpose: the Sentry module and most stores
 * need it, and none of them should pull the user store in through here.
 */
export function isRequestAbort(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false
  }
  const err = error as AxiosError
  const isTimeout =
    err.code === 'ECONNABORTED' && AXIOS_TIMEOUT_MESSAGE.test(err.message ?? '')
  if (
    !isTimeout &&
    (err.code === 'ERR_CANCELED' ||
      err.code === 'ECONNABORTED' ||
      err.message === 'Request aborted' ||
      err.name === 'CanceledError' ||
      err.name === 'AbortError')
  ) {
    return true
  }
  // An HTTP answer is not a stop, even if the signal was aborted afterwards.
  return !err.response && err.config?.signal?.aborted === true
}
